import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Card, DonutChart, BarChart, LineChart,
    Metric, Text, Title, Badge
} from '@tremor/react';
import {
    ClockIcon, CursorArrowRaysIcon,
    VideoCameraIcon, QuestionMarkCircleIcon,
    ArrowPathIcon, ChevronDownIcon, ChevronUpIcon, UserCircleIcon, CheckIcon,
    ArrowsPointingOutIcon, ArrowsPointingInIcon,
    PlayIcon, PauseIcon, SpeakerWaveIcon, SpeakerXMarkIcon, SparklesIcon
} from '@heroicons/react/24/outline';
import { useSidebar } from '../../../../Components/ContextProviders/SidebarContextProvider';
import { getSidebarPadding } from '../../../../utils/sidebar';
import SectionHeader from '../../../../Components/GeneralComponents/SectionHeader';
import {
    getQuestionnaireAnalytics,
    getQuestionnaireAnswers,
    getTeamParticipants,
    createTeamParticipant,
    linkResponseParticipant,
    getResponseRecordings,
    getQuestionnaireGazeOverview,
    getQuestionnaireConstructs,
    getQuestionnaireInsights,
    generateQuestionnaireInsights
} from '../../../../Requests/QuestionnaireRequests';
import { startQuestionnaireVideoEmotionAnalysis, getQuestionnaireVideoEmotionAggregate } from '../../../../Requests/QuestionnaireVideoEmotionRequests';
import QuestionnaireVideoEmotionCard from '../../../../Components/Questionnaire/QuestionnaireVideoEmotionCard';

function formatMs(ms) {
    if (!ms && ms !== 0) return '—';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    const s = ms / 1000;
    if (s < 60) return `${s.toFixed(1)}s`;
    const m = Math.floor(s / 60);
    const rem = Math.round(s % 60);
    return `${m}m ${rem}s`;
}

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
}

function StatBox({ label, value, sub }) {
    return (
        <div className="bg-gray-50 rounded-xl p-4">
            <Text className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</Text>
            <Metric className="text-2xl">{value}</Metric>
            {sub && <Text className="text-xs text-gray-400 mt-1">{sub}</Text>}
        </div>
    );
}

function MousePathCanvas({ events }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !events?.length) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const { width, height } = canvas;

        ctx.clearRect(0, 0, width, height);

        const moves = events.filter(e => e.type === 'move' || e.type === 'click');
        if (moves.length > 1) {
            ctx.beginPath();
            ctx.strokeStyle = 'rgba(59, 130, 246, 0.45)';
            ctx.lineWidth = 1.5;
            ctx.lineJoin = 'round';
            moves.forEach((ev, i) => {
                const px = (ev.x / 100) * width;
                const py = (ev.y / 100) * height;
                if (i === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            });
            ctx.stroke();
        }

        events.filter(e => e.type === 'click').forEach(ev => {
            const px = (ev.x / 100) * width;
            const py = (ev.y / 100) * height;
            ctx.beginPath();
            ctx.arc(px, py, 5, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(239, 68, 68, 0.75)';
            ctx.fill();
            ctx.beginPath();
            ctx.arc(px, py, 9, 0, Math.PI * 2);
            ctx.strokeStyle = 'rgba(239, 68, 68, 0.3)';
            ctx.lineWidth = 1;
            ctx.stroke();
        });

        if (moves.length) {
            const first = moves[0];
            ctx.beginPath();
            ctx.arc((first.x / 100) * width, (first.y / 100) * height, 5, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(34, 197, 94, 0.8)';
            ctx.fill();
        }
    }, [events]);

    return (
        <canvas
            ref={canvasRef}
            width={560}
            height={315}
            className="w-full border border-gray-200 rounded-lg bg-gray-50"
            style={{ aspectRatio: '16/9' }}
        />
    );
}

function MouseOverlayCanvas({ events, videoRef }) {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);

    useEffect(() => {
        const video = videoRef?.current;
        if (!video || !events?.length) return;

        const allMoves = events.filter(e => e.type === 'move' || e.type === 'click');

        const draw = () => {
            rafRef.current = requestAnimationFrame(draw);
            const canvas = canvasRef.current;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');
            const { width, height } = canvas;
            const currentMs = video.currentTime * 1000;

            ctx.clearRect(0, 0, width, height);

            // Fading trail: last 2 seconds
            const trailMs = 2000;
            const trailEvents = allMoves.filter(e => e.t >= currentMs - trailMs && e.t <= currentMs);
            for (let i = 1; i < trailEvents.length; i++) {
                const prev = trailEvents[i - 1];
                const curr = trailEvents[i];
                const alpha = (1 - (currentMs - curr.t) / trailMs) * 0.65;
                ctx.beginPath();
                ctx.strokeStyle = `rgba(59, 130, 246, ${alpha})`;
                ctx.lineWidth = 2;
                ctx.lineCap = 'round';
                ctx.moveTo((prev.x / 100) * width, (prev.y / 100) * height);
                ctx.lineTo((curr.x / 100) * width, (curr.y / 100) * height);
                ctx.stroke();
            }

            // Click ripples — expand + fade over 800ms
            events
                .filter(e => e.type === 'click' && e.t <= currentMs && currentMs - e.t < 800)
                .forEach(ev => {
                    const progress = (currentMs - ev.t) / 800;
                    const px = (ev.x / 100) * width;
                    const py = (ev.y / 100) * height;

                    ctx.beginPath();
                    ctx.arc(px, py, 6 + progress * 18, 0, Math.PI * 2);
                    ctx.strokeStyle = `rgba(239, 68, 68, ${(1 - progress) * 0.8})`;
                    ctx.lineWidth = 2;
                    ctx.stroke();

                    ctx.beginPath();
                    ctx.arc(px, py, 5, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(239, 68, 68, ${1 - progress * 0.5})`;
                    ctx.fill();
                });

            // Interpolated cursor position
            let prevIdx = -1;
            for (let i = allMoves.length - 1; i >= 0; i--) {
                if (allMoves[i].t <= currentMs) { prevIdx = i; break; }
            }
            if (prevIdx >= 0) {
                const prev = allMoves[prevIdx];
                const next = allMoves[prevIdx + 1];
                let cx = prev.x;
                let cy = prev.y;
                if (next) {
                    const frac = Math.min((currentMs - prev.t) / (next.t - prev.t), 1);
                    cx = prev.x + (next.x - prev.x) * frac;
                    cy = prev.y + (next.y - prev.y) * frac;
                }
                const px = (cx / 100) * width;
                const py = (cy / 100) * height;

                ctx.beginPath();
                ctx.arc(px, py, 10, 0, Math.PI * 2);
                ctx.strokeStyle = 'rgba(59, 130, 246, 0.5)';
                ctx.lineWidth = 2;
                ctx.stroke();

                ctx.beginPath();
                ctx.arc(px, py, 5, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(59, 130, 246, 0.9)';
                ctx.fill();
            }
        };

        // Set canvas intrinsic dimensions from the video's natural size so
        // the coordinate space matches the recorded viewport aspect ratio exactly.
        const startLoop = () => {
            const canvas = canvasRef.current;
            if (canvas && video.videoWidth) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
            }
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(draw);
        };

        if (video.readyState >= 1) {
            startLoop();
        } else {
            video.addEventListener('loadedmetadata', startLoop, { once: true });
        }

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            video.removeEventListener('loadedmetadata', startLoop);
        };
    }, [events, videoRef]);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none rounded-lg"
        />
    );
}

// Live gaze dot synced to video playback — the eye-tracking analogue of
// MouseOverlayCanvas. Gaze samples are {t, x%, y%} (viewport percentages),
// interpolated the same way as the mouse cursor but drawn as an amber "gaze"
// marker with a short fading trail.
function GazeOverlayCanvas({ events, videoRef }) {
    const canvasRef = useRef(null);
    const rafRef = useRef(null);
    const smoothRef = useRef(null); // EMA-smoothed {x,y} in % for coherent motion

    useEffect(() => {
        const video = videoRef?.current;
        if (!video || !events?.length) return;

        // WebGazer routinely predicts a little outside the screen (extrapolation);
        // clamp so a gaze "past the edge" pins to the edge instead of flying off the
        // canvas and reading as a wildly wrong position.
        const clamp = (v) => Math.max(0, Math.min(100, v));
        const points = events;
        smoothRef.current = null;

        const draw = () => {
            rafRef.current = requestAnimationFrame(draw);
            const canvas = canvasRef.current;
            if (!canvas) return;

            // Size the backing store to the DISPLAYED box (× devicePixelRatio), not
            // the video's intrinsic size — otherwise a circle in canvas pixels is
            // stretched into an ellipse when the two aspect ratios differ.
            const dpr = window.devicePixelRatio || 1;
            const cw = Math.round(canvas.clientWidth * dpr);
            const ch = Math.round(canvas.clientHeight * dpr);
            if (!cw || !ch) return; // not laid out yet
            if (canvas.width !== cw || canvas.height !== ch) {
                canvas.width = cw;
                canvas.height = ch;
            }

            const ctx = canvas.getContext('2d');
            const { width, height } = canvas;
            const currentMs = video.currentTime * 1000;

            ctx.clearRect(0, 0, width, height);

            // Marker sizes scale with the recording so the gaze ball is a stable
            // fraction of the screen and large enough to convey WebGazer's
            // inherent positional uncertainty (~150px) rather than false precision.
            // Scale off the smaller axis so the radius is round on any aspect.
            const scale = Math.min(width, height);
            const uR = Math.max(60, scale * 0.09);    // uncertainty halo radius
            const dotR = Math.max(10, scale * 0.02);  // solid centre
            const trailW = Math.max(6, scale * 0.013); // trail thickness

            // Fading trail: last 1.2 seconds of gaze.
            const trailMs = 1200;
            const trail = points.filter(p => p.t >= currentMs - trailMs && p.t <= currentMs);
            for (let i = 1; i < trail.length; i++) {
                const prev = trail[i - 1];
                const curr = trail[i];
                const alpha = (1 - (currentMs - curr.t) / trailMs) * 0.5;
                ctx.beginPath();
                ctx.strokeStyle = `rgba(245, 158, 11, ${alpha})`;
                ctx.lineWidth = trailW;
                ctx.lineCap = 'round';
                ctx.moveTo((clamp(prev.x) / 100) * width, (clamp(prev.y) / 100) * height);
                ctx.lineTo((clamp(curr.x) / 100) * width, (clamp(curr.y) / 100) * height);
                ctx.stroke();
            }

            // Interpolated gaze position.
            let prevIdx = -1;
            for (let i = points.length - 1; i >= 0; i--) {
                if (points[i].t <= currentMs) { prevIdx = i; break; }
            }
            if (prevIdx >= 0) {
                const prev = points[prevIdx];
                const next = points[prevIdx + 1];
                let cx = prev.x;
                let cy = prev.y;
                if (next && next.t > prev.t) {
                    const frac = Math.min((currentMs - prev.t) / (next.t - prev.t), 1);
                    cx = prev.x + (next.x - prev.x) * frac;
                    cy = prev.y + (next.y - prev.y) * frac;
                }
                cx = clamp(cx);
                cy = clamp(cy);
                // Light temporal smoothing to damp raw WebGazer jitter; reset on seek jumps.
                const s = smoothRef.current;
                if (s && Math.abs(s.x - cx) < 40 && Math.abs(s.y - cy) < 40) {
                    cx = s.x + (cx - s.x) * 0.35;
                    cy = s.y + (cy - s.y) * 0.35;
                }
                smoothRef.current = { x: cx, y: cy };
                const px = (cx / 100) * width;
                const py = (cy / 100) * height;

                // Uncertainty ball: soft filled halo + a faint ring at its edge to
                // read as an "area they were looking within", not a pinpoint.
                const grad = ctx.createRadialGradient(px, py, 0, px, py, uR);
                grad.addColorStop(0, 'rgba(245, 158, 11, 0.30)');
                grad.addColorStop(0.7, 'rgba(245, 158, 11, 0.12)');
                grad.addColorStop(1, 'rgba(245, 158, 11, 0)');
                ctx.beginPath();
                ctx.fillStyle = grad;
                ctx.arc(px, py, uR, 0, Math.PI * 2);
                ctx.fill();

                ctx.beginPath();
                ctx.arc(px, py, uR, 0, Math.PI * 2);
                ctx.strokeStyle = 'rgba(245, 158, 11, 0.35)';
                ctx.lineWidth = Math.max(1, trailW * 0.4);
                ctx.stroke();

                // Solid centre marker.
                ctx.beginPath();
                ctx.arc(px, py, dotR, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(245, 158, 11, 0.95)';
                ctx.fill();
                ctx.beginPath();
                ctx.arc(px, py, dotR, 0, Math.PI * 2);
                ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
                ctx.lineWidth = Math.max(2, dotR * 0.25);
                ctx.stroke();
            }
        };

        // The draw loop sizes the canvas from its own displayed box each frame, so
        // it self-corrects on layout/fullscreen/resize without needing videoWidth.
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(draw);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [events, videoRef]);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none rounded-lg"
        />
    );
}

// Static gaze heatmap over the recorded screen. Accumulates every gaze sample
// weighted by dwell time (gap to the next sample) into an intensity buffer using
// additive radial gradients, then maps intensity to a blue→green→yellow→red
// colour ramp — the classic heatmap.js algorithm, self-contained on canvas so it
// composes with the video element like the other overlays. Rendered once.
function GazeHeatmapCanvas({ events, videoRef }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const video = videoRef?.current;
        if (!video || !events?.length) return;

        let attempts = 0;
        const render = () => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            // Size to the DISPLAYED box (not video intrinsic) so blobs stay round
            // and aligned; retry until the canvas has been laid out.
            const dpr = window.devicePixelRatio || 1;
            const width = Math.round(canvas.clientWidth * dpr);
            const height = Math.round(canvas.clientHeight * dpr);
            if ((!width || !height) && attempts < 60) {
                attempts += 1;
                requestAnimationFrame(render);
                return;
            }
            if (!width || !height) return;
            canvas.width = width;
            canvas.height = height;

            // Radius scales with resolution so the map reads well on any size.
            const radius = Math.max(24, Math.round(Math.min(width, height) * 0.06));

            // 1) Accumulate weighted alpha into an offscreen grayscale buffer.
            const buf = document.createElement('canvas');
            buf.width = width;
            buf.height = height;
            const bctx = buf.getContext('2d');

            // Dwell weight = time until the next sample (clamped), normalised.
            let maxDwell = 1;
            for (let i = 0; i < events.length - 1; i++) {
                const d = Math.min(events[i + 1].t - events[i].t, 1000);
                if (d > maxDwell) maxDwell = d;
            }

            const clamp = (v) => Math.max(0, Math.min(100, v));
            for (let i = 0; i < events.length; i++) {
                const p = events[i];
                const next = events[i + 1];
                const dwell = next ? Math.min(next.t - p.t, 1000) : 100;
                const weight = Math.max(0.15, dwell / maxDwell);
                const x = (clamp(p.x) / 100) * width;
                const y = (clamp(p.y) / 100) * height;
                const grad = bctx.createRadialGradient(x, y, 0, x, y, radius);
                grad.addColorStop(0, `rgba(0,0,0,${0.35 * weight})`);
                grad.addColorStop(1, 'rgba(0,0,0,0)');
                bctx.fillStyle = grad;
                bctx.fillRect(x - radius, y - radius, radius * 2, radius * 2);
            }

            // 2) Colourise by the accumulated alpha channel.
            const src = bctx.getImageData(0, 0, width, height);
            const out = bctx.createImageData(width, height);
            const s = src.data;
            const o = out.data;
            for (let i = 0; i < s.length; i += 4) {
                const a = s[i + 3] / 255; // 0..1 intensity
                if (a <= 0.01) continue;
                const t = Math.min(a, 1);
                let r;
                let g;
                let b;
                if (t < 0.25) { r = 0; g = Math.round(t / 0.25 * 255); b = 255; }        // blue→cyan
                else if (t < 0.5) { r = 0; g = 255; b = Math.round((1 - (t - 0.25) / 0.25) * 255); } // cyan→green
                else if (t < 0.75) { r = Math.round((t - 0.5) / 0.25 * 255); g = 255; b = 0; }        // green→yellow
                else { r = 255; g = Math.round((1 - (t - 0.75) / 0.25) * 255); b = 0; }               // yellow→red
                o[i] = r;
                o[i + 1] = g;
                o[i + 2] = b;
                o[i + 3] = Math.round(Math.min(t * 1.6, 0.85) * 255);
            }

            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, width, height);
            ctx.putImageData(out, 0, 0);
        };

        // Render once the canvas is laid out (render() retries until it has a size).
        requestAnimationFrame(render);

        // The heatmap is a one-shot render, so it must re-render whenever the
        // displayed box changes — otherwise the fixed pixel buffer gets stretched
        // by the w-full/h-full CSS (round blobs turn into ellipses). The box
        // changes when the video establishes its intrinsic size (loadedmetadata)
        // and on any layout/window resize.
        const rerender = () => {
            attempts = 0;
            requestAnimationFrame(render);
        };
        video.addEventListener('loadedmetadata', rerender);

        let ro = null;
        if (typeof ResizeObserver !== 'undefined' && canvasRef.current) {
            ro = new ResizeObserver(rerender);
            ro.observe(canvasRef.current);
        }

        return () => {
            video.removeEventListener('loadedmetadata', rerender);
            if (ro) ro.disconnect();
        };
    }, [events, videoRef]);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none rounded-lg"
        />
    );
}

function ScreenshotMousePlayer({ src, mouseEvents, gazeEvents, durationMs }) {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentMs, setCurrentMs] = useState(0);
    const canvasRef = useRef(null);
    const imgRef = useRef(null);
    const rafRef = useRef(null);
    const lastTickRef = useRef(null);
    const currentMsRef = useRef(0);
    const eventsRef = useRef(mouseEvents);
    const gazeRef = useRef(gazeEvents);
    useEffect(() => { eventsRef.current = mouseEvents; }, [mouseEvents]);
    useEffect(() => { gazeRef.current = gazeEvents; }, [gazeEvents]);

    const totalMs = useMemo(() => {
        const streams = [mouseEvents, gazeEvents];
        const maxT = streams.reduce((m, arr) => (arr ?? []).reduce((mm, e) => Math.max(mm, e.t), m), 0);
        return durationMs || maxT + 500 || 5000;
    }, [mouseEvents, gazeEvents, durationMs]);
    const totalMsRef = useRef(totalMs);
    useEffect(() => { totalMsRef.current = totalMs; }, [totalMs]);

    const formatTime = (ms) => {
        const s = Math.floor(ms / 1000);
        return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
    };

    const drawFrame = useCallback((timeMs) => {
        const canvas = canvasRef.current;
        const img = imgRef.current;
        const evts = eventsRef.current;
        const gaze = gazeRef.current;
        if (!canvas || !img?.naturalWidth || (!evts?.length && !gaze?.length)) return;
        if (canvas.width !== img.naturalWidth) canvas.width = img.naturalWidth;
        if (canvas.height !== img.naturalHeight) canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        const { width, height } = canvas;
        ctx.clearRect(0, 0, width, height);

        // Interpolate a point stream's position at timeMs (viewport % → canvas px).
        const interp = (stream) => {
            let prevIdx = -1;
            for (let i = stream.length - 1; i >= 0; i--) { if (stream[i].t <= timeMs) { prevIdx = i; break; } }
            if (prevIdx < 0) return null;
            const prev = stream[prevIdx], next = stream[prevIdx + 1];
            let cx = prev.x, cy = prev.y;
            if (next) {
                const frac = Math.min((timeMs - prev.t) / (next.t - prev.t), 1);
                cx = prev.x + (next.x - prev.x) * frac;
                cy = prev.y + (next.y - prev.y) * frac;
            }
            return { px: (cx / 100) * width, py: (cy / 100) * height };
        };

        // Mouse overlay (blue trail + click ripples + cursor).
        if (evts?.length) {
            const allMoves = evts.filter(e => e.type === 'move' || e.type === 'click');
            const trailMs = 2000;
            const trail = allMoves.filter(e => e.t >= timeMs - trailMs && e.t <= timeMs);
            for (let i = 1; i < trail.length; i++) {
                const alpha = (1 - (timeMs - trail[i].t) / trailMs) * 0.65;
                ctx.beginPath();
                ctx.strokeStyle = `rgba(59,130,246,${alpha})`;
                ctx.lineWidth = 2; ctx.lineCap = 'round';
                ctx.moveTo((trail[i - 1].x / 100) * width, (trail[i - 1].y / 100) * height);
                ctx.lineTo((trail[i].x / 100) * width, (trail[i].y / 100) * height);
                ctx.stroke();
            }
            evts.filter(e => e.type === 'click' && e.t <= timeMs && timeMs - e.t < 800).forEach(ev => {
                const progress = (timeMs - ev.t) / 800;
                const px = (ev.x / 100) * width, py = (ev.y / 100) * height;
                ctx.beginPath();
                ctx.arc(px, py, 6 + progress * 18, 0, Math.PI * 2);
                ctx.strokeStyle = `rgba(239,68,68,${(1 - progress) * 0.8})`;
                ctx.lineWidth = 2; ctx.stroke();
                ctx.beginPath();
                ctx.arc(px, py, 5, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(239,68,68,${1 - progress * 0.5})`; ctx.fill();
            });
            const cursor = interp(allMoves);
            if (cursor) {
                ctx.beginPath(); ctx.arc(cursor.px, cursor.py, 10, 0, Math.PI * 2);
                ctx.strokeStyle = 'rgba(59,130,246,0.5)'; ctx.lineWidth = 2; ctx.stroke();
                ctx.beginPath(); ctx.arc(cursor.px, cursor.py, 5, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(59,130,246,0.9)'; ctx.fill();
            }
        }

        // Gaze overlay — an amber "gaze" dot with a soft halo, interpolated the same
        // way as the cursor, so gaze reads over the screenshot like the mouse does.
        if (gaze?.length) {
            const trailMs = 1200;
            const trail = gaze.filter(e => e.t >= timeMs - trailMs && e.t <= timeMs);
            for (let i = 1; i < trail.length; i++) {
                const alpha = (1 - (timeMs - trail[i].t) / trailMs) * 0.4;
                ctx.beginPath();
                ctx.strokeStyle = `rgba(245,158,11,${alpha})`;
                ctx.lineWidth = 2; ctx.lineCap = 'round';
                ctx.moveTo((trail[i - 1].x / 100) * width, (trail[i - 1].y / 100) * height);
                ctx.lineTo((trail[i].x / 100) * width, (trail[i].y / 100) * height);
                ctx.stroke();
            }
            const g = interp(gaze);
            if (g) {
                const r = Math.max(14, Math.round(Math.min(width, height) * 0.03));
                const halo = ctx.createRadialGradient(g.px, g.py, 0, g.px, g.py, r);
                halo.addColorStop(0, 'rgba(245,158,11,0.5)');
                halo.addColorStop(1, 'rgba(245,158,11,0)');
                ctx.fillStyle = halo;
                ctx.beginPath(); ctx.arc(g.px, g.py, r, 0, Math.PI * 2); ctx.fill();
                ctx.beginPath(); ctx.arc(g.px, g.py, 6, 0, Math.PI * 2);
                ctx.fillStyle = 'rgba(245,158,11,0.95)'; ctx.fill();
            }
        }
    }, []);

    // RAF playback loop
    useEffect(() => {
        if (!isPlaying) { cancelAnimationFrame(rafRef.current); return; }
        const tick = (ts) => {
            if (!lastTickRef.current) lastTickRef.current = ts;
            const delta = ts - lastTickRef.current;
            lastTickRef.current = ts;
            const next = Math.min(currentMsRef.current + delta, totalMsRef.current);
            currentMsRef.current = next;
            setCurrentMs(next);
            drawFrame(next);
            if (next < totalMsRef.current) { rafRef.current = requestAnimationFrame(tick); }
            else { setIsPlaying(false); lastTickRef.current = null; }
        };
        lastTickRef.current = null;
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [isPlaying, drawFrame]);

    // Draw on seek while paused
    useEffect(() => { if (!isPlaying) drawFrame(currentMs); }, [currentMs, isPlaying, drawFrame]);

    const handleSeek = (e) => {
        const v = parseFloat(e.target.value);
        currentMsRef.current = v;
        lastTickRef.current = null;
        setCurrentMs(v);
    };

    const handlePlayPause = () => {
        if (currentMs >= totalMs) { currentMsRef.current = 0; setCurrentMs(0); }
        setIsPlaying(p => !p);
    };

    return (
        <div className="max-w-2xl mx-auto">
            <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-black">
                <img
                    ref={imgRef}
                    src={src}
                    alt="Screen screenshot"
                    className="w-full block select-none"
                    draggable={false}
                    onLoad={() => drawFrame(currentMs)}
                />
                <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
                <div className="absolute bottom-0 left-0 right-0 z-20 px-3 py-2 bg-gradient-to-t from-black/80 to-transparent" onClick={e => e.stopPropagation()}>
                    <input type="range" min={0} max={totalMs} step={16} value={currentMs} onChange={handleSeek}
                        className="w-full h-1 mb-2 cursor-pointer accent-white" />
                    <div className="flex items-center gap-2">
                        <button onClick={handlePlayPause} className="text-white hover:text-gray-300 transition-colors">
                            {isPlaying ? <PauseIcon className="h-5 w-5" /> : <PlayIcon className="h-5 w-5" />}
                        </button>
                        <span className="text-white text-xs tabular-nums">{formatTime(currentMs)} / {formatTime(totalMs)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

const VIEW_MODE_KEYS = [
    { id: 'both',    labelKey: 'Questionnaires.Results.ViewBoth' },
    { id: 'video',   labelKey: 'Questionnaires.Results.ViewVideo' },
    { id: 'mouse',   labelKey: 'Questionnaires.Results.ViewMouse' },
    { id: 'gaze',    labelKey: 'Questionnaires.Results.ViewGaze' },
    { id: 'heatmap', labelKey: 'Questionnaires.Results.ViewHeatmap' },
];

function RecordingViewer({ projectUuid, questionnaireUuid, responseUuid }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeSegment, setActiveSegment] = useState(0);
    const [displayMode, setDisplayMode] = useState('both');
    const [isStartingEmotion, setIsStartingEmotion] = useState(false);
    const videoRef = useRef(null);
    const cameraVideoRef = useRef(null);
    const containerRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [volume, setVolume] = useState(1);

    const formatTime = (s) => {
        if (!s || isNaN(s)) return '0:00';
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
        document.addEventListener('fullscreenchange', onFsChange);
        return () => document.removeEventListener('fullscreenchange', onFsChange);
    }, []);

    // Reset player state when segment changes
    useEffect(() => {
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
    }, [activeSegment]);

    const toggleFullscreen = (e) => {
        e.stopPropagation();
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen().catch(() => {});
        } else {
            document.exitFullscreen();
        }
    };

    const handlePlayPause = (e) => {
        e.stopPropagation();
        const v = videoRef.current;
        if (!v) return;
        v.paused ? v.play() : v.pause();
    };

    const handleSeek = (e) => {
        const t = parseFloat(e.target.value);
        if (videoRef.current) videoRef.current.currentTime = t;
        setCurrentTime(t);
    };

    const handleVolumeChange = (e) => {
        const val = parseFloat(e.target.value);
        setVolume(val);
        setIsMuted(val === 0);
        if (videoRef.current) { videoRef.current.volume = val; videoRef.current.muted = val === 0; }
    };

    const toggleMute = (e) => {
        e.stopPropagation();
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        setIsMuted(v.muted);
    };

    // Sync camera video playback with screen recording
    useEffect(() => {
        const screen = videoRef.current;
        const cam = cameraVideoRef.current;
        if (!screen || !cam) return;

        const onPlay = () => { if (cam.paused) cam.play().catch(() => {}); };
        const onPause = () => { if (!cam.paused) cam.pause(); };
        const onSeeked = () => { cam.currentTime = screen.currentTime; };

        screen.addEventListener('play', onPlay);
        screen.addEventListener('pause', onPause);
        screen.addEventListener('seeked', onSeeked);
        return () => {
            screen.removeEventListener('play', onPlay);
            screen.removeEventListener('pause', onPause);
            screen.removeEventListener('seeked', onSeeked);
        };
    }, [activeSegment, data]);

    useEffect(() => {
        getResponseRecordings(projectUuid, questionnaireUuid, responseUuid)
            .then(d => { setData(d); setActiveSegment(0); })
            .catch(() => setError(t('Questionnaires.Results.ErrorLoadAnalytics')))
            .finally(() => setLoading(false));
    }, [projectUuid, questionnaireUuid, responseUuid]);

    // Poll for emotion analysis updates when any segment is pending
    useEffect(() => {
        const session = data?.sessions?.[0];
        if (!session) return;
        const hasPending = session.segments?.some(s =>
            ['queued', 'processing', 'pending_webhook'].includes(s.video_emotion_status)
        );
        if (!hasPending) return;
        const interval = setInterval(() => {
            getResponseRecordings(projectUuid, questionnaireUuid, responseUuid)
                .then(d => setData(d))
                .catch(() => {});
        }, 8000);
        return () => clearInterval(interval);
    }, [data, projectUuid, questionnaireUuid, responseUuid]);

    const handleStartEmotion = () => {
        setIsStartingEmotion(true);
        startQuestionnaireVideoEmotionAnalysis(projectUuid, questionnaireUuid, responseUuid)
            .then(() => getResponseRecordings(projectUuid, questionnaireUuid, responseUuid))
            .then(d => setData(d))
            .catch(() => {})
            .finally(() => setIsStartingEmotion(false));
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 py-4 text-gray-400">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span className="text-sm">{t('Questionnaires.Results.LoadingResponses')}</span>
            </div>
        );
    }
    if (error) return <Text className="text-red-500 text-sm py-2">{error}</Text>;

    const session = data?.sessions?.[0];
    if (!session) return <Text className="text-gray-400 text-sm py-2">{t('Questionnaires.Results.NoResponsesYet')}</Text>;

    const segments = session.segments ?? [];
    if (!segments.length) return <Text className="text-gray-400 text-sm py-2">{t('Questionnaires.Results.NoResponsesYet')}</Text>;

    const seg = segments[activeSegment] ?? segments[0];
    const hasMouseEvents = (seg.mouse_events?.length ?? 0) > 0;
    const hasGazeEvents = (seg.gaze_events?.length ?? 0) > 0;
    const hasScreen = !!seg.screen_url;
    const hasCamera = !!seg.person_url;
    const hasScreenshot = !!seg.screenshot_url;
    const hasBoth = hasMouseEvents && hasScreen;
    const hasGazeOnScreen = hasGazeEvents && hasScreen;
    // Mobile has no screen video, only a per-question screenshot; gaze (and the
    // heatmap) overlay onto that screenshot the same way mouse does.
    const hasGazeOnScreenshot = hasGazeEvents && hasScreenshot && !hasScreen;
    const clicks = hasMouseEvents ? seg.mouse_events.filter(e => e.type === 'click') : [];
    const moves = hasMouseEvents ? seg.mouse_events.filter(e => e.type === 'move') : [];

    // Which overlay modes this segment actually supports.
    const availableModes = VIEW_MODE_KEYS.filter(({ id }) => {
        if (id === 'both') return hasBoth;
        if (id === 'video') return hasScreen;
        if (id === 'mouse') return hasMouseEvents;
        if (id === 'gaze' || id === 'heatmap') return hasGazeOnScreen || hasGazeOnScreenshot;
        return false;
    });
    // Fall back to the first available mode if the current one isn't offered here.
    const effectiveMode = availableModes.some(m => m.id === displayMode)
        ? displayMode
        : (availableModes[0]?.id ?? 'video');

    const showVideo = hasScreen && ['video', 'both', 'gaze', 'heatmap'].includes(effectiveMode);
    const showMouseOverlay = hasBoth && effectiveMode === 'both';
    const showGazeOverlay = hasGazeOnScreen && effectiveMode === 'gaze';
    const showHeatmap = hasGazeOnScreen && effectiveMode === 'heatmap';
    const showStaticMouse = hasMouseEvents && (!hasScreen || effectiveMode === 'mouse');
    // On the mobile screenshot player, which overlay to draw for the current mode.
    const shotShowMouse = hasMouseEvents && effectiveMode === 'mouse';
    const shotShowGaze = hasGazeOnScreenshot && effectiveMode === 'gaze';
    const shotShowHeatmap = hasGazeOnScreenshot && effectiveMode === 'heatmap';

    // Calibration-quality badge from the session.
    const calStatus = session.gaze_calibration_status;
    const calError = session.gaze_calibration_error_px;

    return (
        <div className="mt-3 space-y-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <Text className="text-xs text-gray-500 uppercase tracking-wide font-semibold">
                        Recording session
                    </Text>
                    {session.screen_recording_enabled && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            {t('Questionnaires.Results.RecordingScreen')}
                        </span>
                    )}
                    {session.camera_recording_enabled && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {t('Questionnaires.Results.RecordingCamera')}
                        </span>
                    )}
                    {calStatus === 'passed' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            {t('Questionnaires.Results.GazeCalibrated', { error: calError })}
                        </span>
                    )}
                    {(calStatus === 'failed' || calStatus === 'skipped') && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                            {t('Questionnaires.Results.GazeLowQuality')}
                        </span>
                    )}
                </div>

                {availableModes.length > 1 && (
                    <div className="flex gap-1 flex-wrap">
                        {availableModes.map(({ id, labelKey }) => (
                            <button
                                key={id}
                                onClick={e => { e.stopPropagation(); setDisplayMode(id); }}
                                className={`text-xs px-3 py-1 rounded border transition-colors ${
                                    effectiveMode === id
                                        ? 'bg-gray-900 text-white border-gray-900'
                                        : 'text-gray-600 border-gray-200 hover:border-gray-400'
                                }`}
                            >
                                {t(labelKey)}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {segments.length > 1 && (
                <div className="flex gap-1.5 flex-wrap">
                    {segments.map((s, i) => (
                        <button
                            key={s.uuid}
                            onClick={e => { e.stopPropagation(); setActiveSegment(i); }}
                            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                                i === activeSegment
                                    ? 'bg-gray-900 text-white border-gray-900'
                                    : 'text-gray-600 border-gray-200 hover:border-gray-400'
                            }`}
                        >
                            Q{(s.question_order ?? i) + 1}
                        </button>
                    ))}
                </div>
            )}

            {showVideo && (
                <div>
                    <div className="flex items-center gap-1.5 mb-2">
                        <VideoCameraIcon className="h-4 w-4 text-purple-500" />
                        <Text className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            {t('Questionnaires.Results.LabelScreenRecording')}{showMouseOverlay && ` ${t('Questionnaires.Results.LabelMouseOverlay')}`}{showGazeOverlay && ` ${t('Questionnaires.Results.LabelGazeOverlay')}`}{showHeatmap && ` ${t('Questionnaires.Results.LabelHeatmapOverlay')}`}
                        </Text>
                    </div>
                    <div className="max-w-2xl mx-auto">
                        <div
                            ref={containerRef}
                            className="relative rounded-lg overflow-hidden border border-gray-200 bg-black group"
                            style={isFullscreen ? { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' } : {}}
                            onClick={handlePlayPause}
                        >
                            <video
                                key={seg.uuid}
                                ref={videoRef}
                                src={seg.screen_url}
                                className="w-full block"
                                style={isFullscreen ? { maxHeight: '100vh', width: 'auto' } : {}}
                                onPlay={() => setIsPlaying(true)}
                                onPause={() => setIsPlaying(false)}
                                onTimeUpdate={() => setCurrentTime(videoRef.current?.currentTime ?? 0)}
                                onLoadedMetadata={() => setDuration(videoRef.current?.duration ?? 0)}
                            />
                            {showMouseOverlay && (
                                <MouseOverlayCanvas events={seg.mouse_events} videoRef={videoRef} />
                            )}
                            {showGazeOverlay && (
                                <GazeOverlayCanvas events={seg.gaze_events} videoRef={videoRef} />
                            )}
                            {showHeatmap && (
                                <GazeHeatmapCanvas events={seg.gaze_events} videoRef={videoRef} />
                            )}
                            {hasCamera && (
                                <div className="absolute top-2 right-2 w-64 h-48 rounded-lg overflow-hidden border-2 border-white/40 shadow-lg bg-black">
                                    <video
                                        key={seg.uuid + '_person_pip'}
                                        ref={cameraVideoRef}
                                        src={seg.person_url}
                                        playsInline
                                        muted
                                        className="w-full h-full object-cover"
                                        style={{ transform: 'scaleX(-1)' }}
                                    />
                                </div>
                            )}

                            {/* Custom controls bar */}
                            <div
                                className="absolute bottom-0 left-0 right-0 z-20 px-3 py-2 bg-gradient-to-t from-black/80 to-transparent"
                                onClick={e => e.stopPropagation()}
                            >
                                {/* Seek bar */}
                                <input
                                    type="range"
                                    min={0}
                                    max={duration || 0}
                                    step={0.1}
                                    value={currentTime}
                                    onChange={handleSeek}
                                    className="w-full h-1 mb-2 cursor-pointer accent-white"
                                />
                                <div className="flex items-center gap-2">
                                    {/* Play / Pause */}
                                    <button onClick={handlePlayPause} className="text-white hover:text-gray-300 transition-colors">
                                        {isPlaying
                                            ? <PauseIcon className="w-5 h-5" />
                                            : <PlayIcon className="w-5 h-5" />
                                        }
                                    </button>

                                    {/* Time */}
                                    <span className="text-white text-xs tabular-nums select-none">
                                        {formatTime(currentTime)} / {formatTime(duration)}
                                    </span>

                                    <div className="flex-1" />

                                    {/* Volume */}
                                    <button onClick={toggleMute} className="text-white hover:text-gray-300 transition-colors">
                                        {isMuted || volume === 0
                                            ? <SpeakerXMarkIcon className="w-4 h-4" />
                                            : <SpeakerWaveIcon className="w-4 h-4" />
                                        }
                                    </button>
                                    <input
                                        type="range"
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        value={isMuted ? 0 : volume}
                                        onChange={handleVolumeChange}
                                        className="w-16 h-1 cursor-pointer accent-white"
                                    />

                                    {/* Fullscreen */}
                                    <button onClick={toggleFullscreen} className="text-white hover:text-gray-300 transition-colors" title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                                        {isFullscreen
                                            ? <ArrowsPointingInIcon className="w-4 h-4" />
                                            : <ArrowsPointingOutIcon className="w-4 h-4" />
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>
                        {showMouseOverlay && (
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                <span>{moves.length} {t('Questionnaires.Results.LabelMoves')}</span>
                                <span className="text-red-400">{clicks.length} {t('Questionnaires.Results.LabelClicks')}</span>
                                {seg.duration_ms && <span>{formatMs(seg.duration_ms)}</span>}
                                <span className="flex items-center gap-1">
                                    <span className="inline-block w-3 h-0.5 bg-blue-400 rounded" /> {t('Questionnaires.Results.LabelTrail')}
                                </span>
                                <span className="flex items-center gap-1">
                                    <span className="inline-block w-2 h-2 rounded-full bg-red-400" /> {t('Questionnaires.Results.LabelClick')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {hasScreenshot && !hasScreen && (
                <div>
                    <div className="flex items-center gap-1.5 mb-2">
                        <VideoCameraIcon className="h-4 w-4 text-purple-500" />
                        <Text className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            {t('Questionnaires.Results.LabelScreenRecording')}
                            {shotShowMouse && ` ${t('Questionnaires.Results.LabelMouseOverlay')}`}
                            {shotShowGaze && ` ${t('Questionnaires.Results.LabelGazeOverlay')}`}
                            {shotShowHeatmap && ` ${t('Questionnaires.Results.LabelHeatmapOverlay')}`}
                        </Text>
                    </div>
                    {shotShowHeatmap ? (
                        // Static gaze heatmap over the still screenshot.
                        <div className="max-w-2xl mx-auto">
                            <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-black">
                                <img src={seg.screenshot_url} alt="" className="w-full block select-none" draggable={false} />
                                <StaticGazeHeatmap points={seg.gaze_events} />
                            </div>
                        </div>
                    ) : (
                        <ScreenshotMousePlayer
                            src={seg.screenshot_url}
                            mouseEvents={shotShowMouse ? seg.mouse_events : null}
                            gazeEvents={shotShowGaze ? seg.gaze_events : null}
                            durationMs={seg.duration_ms}
                        />
                    )}
                    {shotShowMouse && (
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            <span>{moves.length} {t('Questionnaires.Results.LabelMoves')}</span>
                            <span className="text-red-400">{clicks.length} {t('Questionnaires.Results.LabelClicks')}</span>
                            {seg.duration_ms && <span>{formatMs(seg.duration_ms)}</span>}
                            <span className="flex items-center gap-1">
                                <span className="inline-block w-3 h-0.5 bg-blue-400 rounded" /> {t('Questionnaires.Results.LabelTrail')}
                            </span>
                            <span className="flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-full bg-red-400" /> {t('Questionnaires.Results.LabelClick')}
                            </span>
                        </div>
                    )}
                    {shotShowGaze && (
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                            {seg.duration_ms && <span>{formatMs(seg.duration_ms)}</span>}
                            <span className="flex items-center gap-1">
                                <span className="inline-block w-2 h-2 rounded-full bg-amber-400" /> {t('Questionnaires.Results.LabelGazeOverlay')}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {showStaticMouse && !hasScreenshot && (
                <div>
                    <div className="flex items-center gap-1.5 mb-2">
                        <CursorArrowRaysIcon className="h-4 w-4 text-gray-400" />
                        <Text className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('Questionnaires.Results.LabelMousePath')}</Text>
                    </div>
                    <div className="max-w-2xl mx-auto">
                        <MousePathCanvas events={seg.mouse_events} />
                    </div>
                    <div className="flex gap-4 mt-2 text-xs text-gray-400">
                        <span>{moves.length} {t('Questionnaires.Results.LabelMoves')}</span>
                        <span className="text-red-400">{clicks.length} {t('Questionnaires.Results.LabelClicks')}</span>
                        {seg.duration_ms && <span>{formatMs(seg.duration_ms)}</span>}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                            <span className="inline-block w-3 h-0.5 bg-blue-400 rounded" /> {t('Questionnaires.Results.LabelPath')}
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="inline-block w-2 h-2 rounded-full bg-red-400" /> {t('Questionnaires.Results.LabelClick')}
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="inline-block w-2 h-2 rounded-full bg-green-400" /> {t('Questionnaires.Results.LabelStart')}
                        </span>
                    </div>
                </div>
            )}

            {hasCamera && !hasScreen && (
                <div>
                    <div className="flex items-center gap-1.5 mb-2">
                        <UserCircleIcon className="h-4 w-4 text-blue-500" />
                        <Text className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('Questionnaires.Results.LabelCameraRecording')}</Text>
                    </div>
                    <div className="max-w-2xl mx-auto">
                        <video
                            key={seg.uuid + '_person'}
                            src={seg.person_url}
                            controls
                            playsInline
                            className="w-full block rounded-lg border border-gray-200 bg-black"
                        />
                    </div>
                </div>
            )}

            {!hasScreen && !hasScreenshot && !hasMouseEvents && !hasCamera && (
                <Text className="text-xs text-gray-400 py-2">{t('Questionnaires.Results.NoRecordingData')}</Text>
            )}

            <QuestionnaireVideoEmotionCard
                session={session}
                projectUuid={projectUuid}
                questionnaireUuid={questionnaireUuid}
                responseUuid={responseUuid}
                onStartAnalysis={handleStartEmotion}
                isStarting={isStartingEmotion}
            />
        </div>
    );
}

// Table answers are stored as a JSON array of selections; everything else is plain text.
function formatAnswerValue(value) {
    if (value == null || value === '') return null;
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) return parsed.map(v => (v == null ? '—' : v)).join(', ');
    } catch {
        // not JSON — plain text answer
    }
    return value;
}

// Every choice the question offered, with the ones this participant picked marked.
function OptionList({ options, selected = [] }) {
    if (!options?.length) return null;

    return (
        <ul className="space-y-1">
            {options.map((option, i) => {
                const chosen = selected.includes(option);
                return (
                    <li
                        key={`${option}-${i}`}
                        className={`flex items-center gap-2 text-sm ${chosen ? 'text-gray-900 font-medium' : 'text-gray-400'}`}
                    >
                        <span
                            className={`h-4 w-4 shrink-0 rounded-full border flex items-center justify-center ${
                                chosen ? 'border-iavisus-100 bg-iavisus-100' : 'border-gray-300'
                            }`}
                        >
                            {chosen && <CheckIcon className="h-2.5 w-2.5 text-white" />}
                        </span>
                        {option}
                    </li>
                );
            })}
        </ul>
    );
}

// Table answers store one column id per row; the grid shows the whole matrix.
function TableAnswer({ tableData, value }) {
    const rows = tableData?.rows ?? [];
    const columns = tableData?.columns ?? [];

    let selections = [];
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) selections = parsed;
    } catch {
        // no selections recorded
    }

    if (rows.length === 0 || columns.length === 0) return null;

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-100 rounded-lg">
                <thead>
                    <tr className="bg-gray-50">
                        <th className="px-3 py-1.5" />
                        {columns.map(column => (
                            <th key={column.id} className="px-3 py-1.5 text-xs font-medium text-gray-500">{column.text}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={row.id} className="border-t border-gray-100">
                            <td className="px-3 py-1.5 text-gray-700">{row.text}</td>
                            {columns.map(column => (
                                <td key={column.id} className="px-3 py-1.5 text-center">
                                    {selections[rowIndex] === column.id ? (
                                        <span className="inline-block h-3 w-3 rounded-full bg-iavisus-100" />
                                    ) : (
                                        <span className="inline-block h-3 w-3 rounded-full border border-gray-200" />
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function AnswerBody({ answer, prompt, compact = false }) {
    const { t } = useTranslation();
    const value = formatAnswerValue(answer.value);

    if (prompt?.type === 'table' && prompt.table_data) {
        return <TableAnswer tableData={prompt.table_data} value={answer.value} />;
    }

    if (!compact && prompt?.options?.length > 0) {
        // Multi-select answers arrive comma-joined.
        const selected = value ? value.split(', ') : [];
        return <OptionList options={prompt.options} selected={selected} />;
    }

    return (
        <div className="space-y-2">
            {answer.audio_url && <audio src={answer.audio_url} controls className="w-full" />}
            {answer.skipped && !value ? (
                <Text className="text-xs text-amber-600">{t('Questionnaires.Results.AnswerSkipped')}</Text>
            ) : value ? (
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{value}</p>
            ) : answer.audio_url ? (
                <Text className="text-xs text-gray-500">
                    {answer.transcription_status === 'failed'
                        ? t('Questionnaires.Results.TranscriptionFailed')
                        : t('Questionnaires.Results.Transcribing')}
                </Text>
            ) : (
                <Text className="text-xs text-gray-400">{t('Questionnaires.Results.NoAnswerGiven')}</Text>
            )}
        </div>
    );
}

function AnswerLabel({ children }) {
    return <Text className="text-xs text-gray-500 uppercase tracking-wide mb-1">{children}</Text>;
}

/**
 * Attributes one response to a team-wide participant identifier. Subjects are
 * shared across projects and sources, so linking here also ties this response to
 * the same person's audios, texts and calls.
 */
function ParticipantPicker({ projectUuid, questionnaireUuid, participant, participants, onLinked, onCreated }) {
    const { t } = useTranslation();
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [error, setError] = useState(null);

    const linked = participant.participant;

    const link = async (participantUuid) => {
        setSaving(true);
        setError(null);
        try {
            const data = await linkResponseParticipant(projectUuid, questionnaireUuid, participant.response_uuid, participantUuid);
            onLinked(participant.response_uuid, data.participant);
        } catch {
            setError(t('Questionnaires.Results.ParticipantLinkFailed'));
        }
        setSaving(false);
    };

    const create = async () => {
        if (!newName.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const data = await createTeamParticipant(projectUuid, { name: newName.trim(), email: newEmail.trim() || null });
            onCreated(data.participant);
            await link(data.participant.uuid);
            setCreating(false);
            setNewName('');
            setNewEmail('');
        } catch {
            setError(t('Questionnaires.Results.ParticipantCreateFailed'));
            setSaving(false);
        }
    };

    return (
        <div className="mt-4 border-t border-gray-100 pt-4">
            <AnswerLabel>{t('Questionnaires.Results.ParticipantIdentity')}</AnswerLabel>

            <div className="flex flex-wrap items-center gap-2">
                <select
                    className="rounded-md border-gray-300 text-sm shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                    value={linked?.uuid || ''}
                    disabled={saving}
                    onChange={(e) => link(e.target.value || null)}
                >
                    <option value="">{t('Questionnaires.Results.ParticipantUnassigned')}</option>
                    {participants.map(p => (
                        <option key={p.uuid} value={p.uuid}>
                            {p.code} · {p.name}{p.email ? ` (${p.email})` : ''}
                        </option>
                    ))}
                </select>

                {linked && (
                    <Badge color={linked.link_method === 'email' ? 'blue' : 'gray'} size="xs">
                        {linked.code} · {linked.link_method === 'email'
                            ? t('Questionnaires.Results.ParticipantAutoLinked')
                            : t('Questionnaires.Results.ParticipantManualLinked')}
                    </Badge>
                )}

                {!creating && (
                    <button
                        type="button"
                        onClick={() => setCreating(true)}
                        className="text-xs font-medium text-iavisus-100 hover:underline"
                    >
                        {t('Questionnaires.Results.ParticipantCreateNew')}
                    </button>
                )}
            </div>

            {creating && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <input
                        className="rounded-md border-gray-300 text-sm shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                        placeholder={t('Questionnaires.Results.ParticipantNamePlaceholder')}
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                    />
                    <input
                        className="rounded-md border-gray-300 text-sm shadow-sm focus:border-iavisus-100 focus:ring-iavisus-100"
                        placeholder={t('Questionnaires.Results.ParticipantEmailPlaceholder')}
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                    />
                    <button
                        type="button"
                        onClick={create}
                        disabled={saving || !newName.trim()}
                        className="rounded-md bg-iavisus-100 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                    >
                        {t('Questionnaires.Results.ParticipantSave')}
                    </button>
                    <button
                        type="button"
                        onClick={() => setCreating(false)}
                        className="text-xs text-gray-500 hover:underline"
                    >
                        {t('Questionnaires.Results.ParticipantCancel')}
                    </button>
                </div>
            )}

            {error && <Text className="text-xs text-red-500 mt-2">{error}</Text>}
        </div>
    );
}

/**
 * Individual answers, pivotable between one card per participant and one card per
 * question. Both views read the same payload, so switching costs no request.
 */
function ResponsesTab({ projectUuid, questionnaireUuid }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [groupBy, setGroupBy] = useState('participant');
    const [openParticipant, setOpenParticipant] = useState(null);
    const [teamParticipants, setTeamParticipants] = useState([]);

    useEffect(() => {
        getQuestionnaireAnswers(projectUuid, questionnaireUuid)
            .then(setData)
            .catch(() => setError(t('Questionnaires.Results.FailedToLoadResponses')))
            .finally(() => setLoading(false));

        getTeamParticipants(projectUuid)
            .then(payload => setTeamParticipants(payload.participants ?? []))
            .catch(() => setTeamParticipants([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectUuid, questionnaireUuid]);

    // Keep the loaded payload in step with a link change instead of refetching it.
    const applyLink = (responseUuid, linkedParticipant) => {
        setData(prev => ({
            ...prev,
            participants: prev.participants.map(p =>
                p.response_uuid === responseUuid
                    ? { ...p, participant: linkedParticipant, label: linkedParticipant?.name || p.label }
                    : p
            ),
        }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400 mr-2" />
                <Text>{t('Questionnaires.Results.LoadingResponses')}</Text>
            </div>
        );
    }

    if (error) return <Text className="text-red-500 text-center py-12">{error}</Text>;

    const participants = data?.participants ?? [];
    const questions = data?.questions ?? [];

    if (participants.length === 0) {
        return <Text className="text-gray-500 text-center py-12">{t('Questionnaires.Results.NoResponsesYet')}</Text>;
    }

    const statusColor = (status) =>
        status === 'completed' ? 'green' : status === 'in_progress' ? 'amber' : 'gray';

    const toggle = (
        <div className="flex items-center justify-between mb-4">
            <Text className="text-xs text-gray-500">
                {t('Questionnaires.Results.ParticipantsCount', { count: participants.length })}
            </Text>
            <div className="inline-flex rounded-md border border-iavisus-100 p-0.5">
                {[
                    ['participant', t('Questionnaires.Results.GroupByParticipant')],
                    ['question', t('Questionnaires.Results.GroupByQuestion')],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setGroupBy(key)}
                        className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                            groupBy === key ? 'bg-iavisus-100 text-white' : 'text-iavisus-100 hover:bg-gray-50'
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>
        </div>
    );

    if (groupBy === 'participant') {
        return (
            <div>
                {toggle}
                <div className="space-y-3">
                    {participants.map(participant => {
                        const isOpen = openParticipant === participant.response_uuid;
                        return (
                            <Card key={participant.response_uuid} className="py-3">
                                <button
                                    className="w-full flex items-center justify-between text-left"
                                    onClick={() => setOpenParticipant(isOpen ? null : participant.response_uuid)}
                                >
                                    <div className="flex items-center gap-3">
                                        <UserCircleIcon className="h-5 w-5 text-gray-400" />
                                        <span className="text-sm font-medium text-gray-900">{participant.label}</span>
                                        {participant.participant && (
                                            <Badge color="indigo" size="xs">{participant.participant.code}</Badge>
                                        )}
                                        <Badge color={statusColor(participant.status)} size="xs">{participant.status}</Badge>
                                        {participant.has_recording && (
                                            <span className="flex items-center gap-1 text-xs text-gray-500">
                                                <CursorArrowRaysIcon className="h-4 w-4" /> {t('Questionnaires.Results.RecordingMouse')}
                                            </span>
                                        )}
                                        {participant.has_screen_recording && (
                                            <span className="flex items-center gap-1 text-xs text-purple-600">
                                                <VideoCameraIcon className="h-4 w-4" /> {t('Questionnaires.Results.RecordingScreen')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Text className="text-xs text-gray-500">{formatDate(participant.started_at)}</Text>
                                        <Text className="text-xs text-gray-500">{formatMs(participant.total_duration)}</Text>
                                        <Text className="text-xs text-gray-500">
                                            {t('Questionnaires.Results.AnswersCount', { count: participant.answers.length })}
                                        </Text>
                                        {isOpen
                                            ? <ChevronUpIcon className="h-4 w-4 text-gray-400" />
                                            : <ChevronDownIcon className="h-4 w-4 text-gray-400" />}
                                    </div>
                                </button>

                                {isOpen && (
                                    <div className="mt-4 space-y-4 border-t border-gray-100 pt-4">
                                        {participant.answers.length === 0 && (
                                            <Text className="text-xs text-gray-400">{t('Questionnaires.Results.NoAnswersRecorded')}</Text>
                                        )}
                                        {participant.answers.map((answer, i) => {
                                            const question = questions.find(q => q.uuid === answer.question_uuid);
                                            const prompt = answer.section_uuid
                                                ? question?.sections?.find(section => section.uuid === answer.section_uuid)
                                                : question;
                                            return (
                                                <div key={`${answer.question_uuid}-${answer.section_uuid || i}`}>
                                                    {answer.section_uuid && question?.question_text && (
                                                        <AnswerLabel>{question.question_text}</AnswerLabel>
                                                    )}
                                                    <p className="text-sm font-medium text-gray-900 mb-1">
                                                        {prompt?.title || prompt?.question_text || answer.section_title}
                                                    </p>
                                                    {prompt?.description && (
                                                        <Text className="text-xs text-gray-500 mb-2">{prompt.description}</Text>
                                                    )}
                                                    <AnswerBody answer={answer} prompt={prompt} />
                                                </div>
                                            );
                                        })}

                                        <ParticipantPicker
                                            projectUuid={projectUuid}
                                            questionnaireUuid={questionnaireUuid}
                                            participant={participant}
                                            participants={teamParticipants}
                                            onLinked={applyLink}
                                            onCreated={(created) => setTeamParticipants(prev => [...prev, created])}
                                        />

                                        {(participant.has_recording || participant.has_screen_recording) && (
                                            <RecordingViewer
                                                projectUuid={projectUuid}
                                                questionnaireUuid={questionnaireUuid}
                                                responseUuid={participant.response_uuid}
                                            />
                                        )}
                                    </div>
                                )}
                            </Card>
                        );
                    })}
                </div>
            </div>
        );
    }

    const answersByKey = new Map();
    participants.forEach(participant => {
        participant.answers.forEach(answer => {
            const key = `${answer.question_uuid}::${answer.section_uuid || ''}`;
            if (!answersByKey.has(key)) answersByKey.set(key, []);
            answersByKey.get(key).push({ ...answer, participant });
        });
    });

    return (
        <div>
            {toggle}
            <div className="space-y-4">
                {questions.flatMap(question => {
                    const entries = question.sections?.length > 0
                        ? question.sections.map(section => ({
                            key: `${question.uuid}::${section.uuid}`,
                            title: section.title,
                            parentText: question.question_text,
                            prompt: section,
                        }))
                        : [{ key: `${question.uuid}::`, title: question.question_text, parentText: null, prompt: question }];

                    return entries.map(({ key, title, parentText, prompt }) => {
                        const answers = answersByKey.get(key) ?? [];
                        return (
                            <Card key={key}>
                                <div className="flex items-start justify-between mb-3">
                                    <div className="pr-4">
                                        {parentText && parentText !== title && (
                                            <AnswerLabel>{parentText}</AnswerLabel>
                                        )}
                                        <p className="font-medium text-gray-900 text-sm">{title}</p>
                                        {prompt?.description && (
                                            <Text className="text-xs text-gray-500 mt-1">{prompt.description}</Text>
                                        )}
                                    </div>
                                    <Text className="text-xs text-gray-500 shrink-0">
                                        {t('Questionnaires.Results.AnswersCount', { count: answers.length })}
                                    </Text>
                                </div>

                                {(prompt?.options?.length > 0 || prompt?.table_data) && (
                                    <div className="mb-3 pb-3 border-b border-gray-100">
                                        {prompt.table_data
                                            ? <TableAnswer tableData={prompt.table_data} value={null} />
                                            : <OptionList options={prompt.options} />}
                                    </div>
                                )}
                                {answers.length === 0 ? (
                                    <Text className="text-xs text-gray-400">{t('Questionnaires.Results.NoAnswersRecorded')}</Text>
                                ) : (
                                    <div className="space-y-3">
                                        {answers.map((answer, i) => (
                                            <div key={`${answer.participant.response_uuid}-${i}`} className="border-l-2 border-gray-200 pl-3">
                                                <AnswerLabel>{answer.participant.label}</AnswerLabel>
                                                <AnswerBody answer={answer} prompt={prompt} compact />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card>
                        );
                    });
                })}
            </div>
        </div>
    );
}

// Phase 4 — the LLM research-insight narrative synthesised from the construct
// evidence. Generate-on-demand (LLM cost), polls while the job runs.
function OverviewInsightsCard({ analytics, projectUuid, questionnaireUuid }) {
    const { t } = useTranslation();
    const [insight, setInsight] = useState(null);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const pollRef = useRef(null);
    const aliveRef = useRef(true);

    const poll = useCallback(() => {
        pollRef.current = setTimeout(async () => {
            const r = await getQuestionnaireInsights(projectUuid, questionnaireUuid).catch(() => null);
            if (!aliveRef.current || !r) return;
            setInsight(r);
            if (r.status === 'generating') poll();
        }, 4000);
    }, [projectUuid, questionnaireUuid]);

    useEffect(() => {
        aliveRef.current = true;
        if (!projectUuid || !questionnaireUuid) return undefined;
        getQuestionnaireInsights(projectUuid, questionnaireUuid)
            .then((r) => {
                if (!aliveRef.current) return;
                setInsight(r);
                setLoading(false);
                if (r.status === 'generating') poll();
            })
            .catch(() => { if (aliveRef.current) setLoading(false); });
        return () => { aliveRef.current = false; if (pollRef.current) clearTimeout(pollRef.current); };
    }, [projectUuid, questionnaireUuid, poll]);

    const generate = async () => {
        setWorking(true);
        try {
            await generateQuestionnaireInsights(projectUuid, questionnaireUuid);
            setInsight({ status: 'generating' });
            poll();
        } catch (e) {
            // leave state; user can retry
        } finally {
            setWorking(false);
        }
    };

    if (loading) return null;

    const status = insight?.status ?? 'none';
    const content = insight?.content;

    // Map question_id -> "Q{n}" for insight references.
    const qIndex = {};
    (analytics?.question_analytics ?? []).forEach((qa, i) => { qIndex[qa.id] = i + 1; });

    const confPill = (c) => {
        const map = { high: 'bg-emerald-50 text-emerald-700', medium: 'bg-amber-50 text-amber-700', low: 'bg-gray-100 text-gray-500' };
        return <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${map[c] || map.low}`}>{c}</span>;
    };

    const busy = status === 'generating' || working;

    return (
        <Card className="border-indigo-100">
            <div className="flex items-start justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                    <SparklesIcon className="h-4 w-4 text-indigo-500" />
                    <Title className="text-sm font-semibold">{t('Questionnaires.Results.SectionInsights')}</Title>
                </div>
                <button
                    onClick={generate}
                    disabled={busy}
                    className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-indigo-300 transition-colors flex items-center gap-1.5 shrink-0"
                >
                    {busy && <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />}
                    {status === 'ready' ? t('Questionnaires.Results.InsightsRegenerate') : t('Questionnaires.Results.InsightsGenerate')}
                </button>
            </div>

            {status === 'generating' && (
                <Text className="text-sm text-gray-500 py-3">{t('Questionnaires.Results.InsightsGenerating')}</Text>
            )}
            {status === 'insufficient_data' && (
                <Text className="text-sm text-gray-400 py-3">{t('Questionnaires.Results.InsightsInsufficient')}</Text>
            )}
            {status === 'failed' && (
                <Text className="text-sm text-rose-500 py-3">{t('Questionnaires.Results.InsightsFailed')}</Text>
            )}
            {status === 'none' && (
                <Text className="text-sm text-gray-400 py-3">{t('Questionnaires.Results.InsightsNone')}</Text>
            )}

            {status === 'ready' && content && (
                <div className="space-y-4 mt-2">
                    {content.summary && <Text className="text-sm text-gray-700 leading-relaxed">{content.summary}</Text>}

                    {(content.insights ?? []).length > 0 && (
                        <div className="space-y-3">
                            {content.insights.map((ins, i) => (
                                <div key={i} className="border-l-2 border-indigo-200 pl-3">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-gray-800">{ins.title || `Insight ${i + 1}`}</span>
                                        {confPill(ins.confidence)}
                                        {ins.question_id && qIndex[ins.question_id] && (
                                            <span className="text-[10px] text-gray-400">Q{qIndex[ins.question_id]}</span>
                                        )}
                                    </div>
                                    <Text className="text-sm text-gray-600 mt-0.5">{ins.finding}</Text>
                                    {ins.evidence && <Text className="text-xs text-gray-400 mt-0.5 italic">{ins.evidence}</Text>}
                                </div>
                            ))}
                        </div>
                    )}

                    {(content.recommendations ?? []).length > 0 && (
                        <div>
                            <Text className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{t('Questionnaires.Results.InsightsRecommendations')}</Text>
                            <ul className="space-y-1">
                                {content.recommendations.map((rec, i) => (
                                    <li key={i} className="text-sm text-gray-600 flex gap-2">
                                        <span className="text-indigo-400 mt-0.5">→</span>
                                        <span>{rec}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <Text className="text-[11px] text-gray-400 pt-1">{t('Questionnaires.Results.InsightsCaveat')}</Text>
                </div>
            )}
        </Card>
    );
}

// Confidence dot — how much the underlying data supports a construct score.
function ConfidenceDot({ value }) {
    const c = value == null ? 0 : value;
    const color = c >= 0.66 ? 'bg-emerald-500' : c >= 0.33 ? 'bg-amber-500' : 'bg-gray-300';
    return <span className={`inline-block w-1.5 h-1.5 rounded-full ${color}`} title={`confidence ${Math.round(c * 100)}%`} />;
}

// Phase 3 — per-question cognitive-affective constructs. Effort/conflict are
// shown as the share of participants who were *elevated* (the per-question mean
// sits at the norm by construction); attention is an absolute score; mood is
// facial valence when available. Each cell carries a data-confidence dot.
function OverviewConstructs({ analytics, projectUuid, questionnaireUuid }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!projectUuid || !questionnaireUuid) return undefined;
        let cancelled = false;
        getQuestionnaireConstructs(projectUuid, questionnaireUuid)
            .then((res) => { if (!cancelled) { setData(res); setLoading(false); } })
            .catch(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [projectUuid, questionnaireUuid]);

    if (loading || !data) return null;
    const questions = (data.questions ?? []).filter(q => q.constructs);
    if (questions.length === 0) return null;

    const qText = {};
    (analytics?.question_analytics ?? []).forEach((qa) => { qText[qa.id] = qa.question_text; });

    const share = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`);
    const moodLabel = (v) => {
        if (v == null) return { text: '—', cls: 'text-gray-400' };
        if (v > 0.15) return { text: t('Questionnaires.Results.MoodPositive'), cls: 'text-emerald-600' };
        if (v < -0.15) return { text: t('Questionnaires.Results.MoodNegative'), cls: 'text-rose-600' };
        return { text: t('Questionnaires.Results.MoodNeutral'), cls: 'text-gray-500' };
    };

    return (
        <Card>
            <div className="flex items-center gap-2 mb-1">
                <Title className="text-sm font-semibold">{t('Questionnaires.Results.SectionConstructs')}</Title>
            </div>
            <Text className="text-xs text-gray-400 mb-4">{t('Questionnaires.Results.ConstructsDesc')}</Text>

            <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                    <thead>
                        <tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
                            <th className="text-left font-medium py-2 pr-3">{t('Questionnaires.Results.ColQuestion')}</th>
                            <th className="text-left font-medium py-2 px-2">{t('Questionnaires.Results.ColAttention')}</th>
                            <th className="text-left font-medium py-2 px-2">{t('Questionnaires.Results.ColEffort')}</th>
                            <th className="text-left font-medium py-2 px-2">{t('Questionnaires.Results.ColConflict')}</th>
                            <th className="text-left font-medium py-2 px-2">{t('Questionnaires.Results.ColMood')}</th>
                            <th className="text-right font-medium py-2 pl-2">{t('Questionnaires.Results.ColResponses')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {questions.map((q, i) => {
                            const c = q.constructs;
                            const attn = c.attention?.mean_score;
                            const mood = moodLabel(c.valence?.mean_value);
                            return (
                                <tr key={q.question_id} className="border-b border-gray-50">
                                    <td className="py-2.5 pr-3 max-w-[220px]">
                                        <span className="text-gray-700 line-clamp-1">{`Q${i + 1}. ${qText[q.question_id] ?? ''}`}</span>
                                    </td>
                                    <td className="py-2.5 px-2">
                                        <div className="flex items-center gap-2">
                                            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div className="h-full bg-gray-800 rounded-full" style={{ width: `${attn ?? 0}%` }} />
                                            </div>
                                            <span className="tabular-nums text-gray-600 text-xs w-7">{attn ?? '—'}</span>
                                            <ConfidenceDot value={c.attention?.mean_confidence} />
                                        </div>
                                    </td>
                                    <td className="py-2.5 px-2">
                                        <span className="tabular-nums text-gray-700 mr-1.5">{share(c.cognitive_load?.elevated_share)}</span>
                                        <ConfidenceDot value={c.cognitive_load?.mean_confidence} />
                                    </td>
                                    <td className="py-2.5 px-2">
                                        <span className="tabular-nums text-gray-700 mr-1.5">{share(c.decision_conflict?.elevated_share)}</span>
                                        <ConfidenceDot value={c.decision_conflict?.mean_confidence} />
                                    </td>
                                    <td className="py-2.5 px-2">
                                        <span className={`${mood.cls} mr-1.5`}>{mood.text}</span>
                                        {c.valence?.n > 0 && <ConfidenceDot value={c.valence?.mean_confidence} />}
                                    </td>
                                    <td className="py-2.5 pl-2 text-right tabular-nums text-gray-500">{q.response_count}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <Text className="text-[11px] text-gray-400 mt-3 leading-relaxed">
                {t('Questionnaires.Results.ConstructsCaveat')}
            </Text>
        </Card>
    );
}

// Static aggregate gaze heatmap drawn over a still image. Unlike GazeHeatmapCanvas
// (which weights by per-sample dwell for one participant's playback), this takes a
// flat list of {x, y} viewport-percentage points pooled across all participants and
// relies on additive density — where many people looked accumulates into hotter
// colour. Re-renders on resize so round blobs never get stretched by the CSS box.
function StaticGazeHeatmap({ points }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!points?.length) return undefined;

        const render = () => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const dpr = window.devicePixelRatio || 1;
            const width = Math.round(canvas.clientWidth * dpr);
            const height = Math.round(canvas.clientHeight * dpr);
            if (!width || !height) return;
            canvas.width = width;
            canvas.height = height;

            // Accumulate intensity on a small grid (fast + inherently smooth once
            // upscaled), with a soft radial falloff per point. Working on a Float
            // grid — instead of an 8-bit canvas — is what lets us measure the true
            // peak density and NORMALISE against it, so only genuine hotspots read
            // red rather than the whole frame saturating.
            const GRID_MAX = 240;
            const scale = Math.min(1, GRID_MAX / Math.max(width, height));
            const gw = Math.max(1, Math.round(width * scale));
            const gh = Math.max(1, Math.round(height * scale));
            const intensity = new Float32Array(gw * gh);
            const radius = Math.max(5, Math.round(Math.min(gw, gh) * 0.05));
            const r2 = radius * radius;
            const clamp = (v) => Math.max(0, Math.min(100, v));

            for (let i = 0; i < points.length; i++) {
                const cx = (clamp(points[i].x) / 100) * gw;
                const cy = (clamp(points[i].y) / 100) * gh;
                const x0 = Math.max(0, Math.floor(cx - radius));
                const x1 = Math.min(gw - 1, Math.ceil(cx + radius));
                const y0 = Math.max(0, Math.floor(cy - radius));
                const y1 = Math.min(gh - 1, Math.ceil(cy + radius));
                for (let yy = y0; yy <= y1; yy++) {
                    for (let xx = x0; xx <= x1; xx++) {
                        const dx = xx - cx;
                        const dy = yy - cy;
                        const d2 = dx * dx + dy * dy;
                        if (d2 > r2) continue;
                        const fall = 1 - Math.sqrt(d2) / radius; // 1 at centre → 0 at edge
                        intensity[yy * gw + xx] += fall * fall;
                    }
                }
            }

            let max = 0;
            for (let i = 0; i < intensity.length; i++) {
                if (intensity[i] > max) max = intensity[i];
            }
            if (max <= 0) return;

            // Colourise normalised intensity (0..1) via a blue→green→yellow→red ramp.
            const grid = document.createElement('canvas');
            grid.width = gw;
            grid.height = gh;
            const gctx = grid.getContext('2d');
            const img = gctx.createImageData(gw, gh);
            const o = img.data;
            for (let i = 0; i < intensity.length; i++) {
                let tt = intensity[i] / max;
                if (tt <= 0.03) continue;
                tt = Math.pow(tt, 0.65); // lift mid-tones so it's not mostly blue
                let r;
                let g;
                let b;
                if (tt < 0.25) { r = 0; g = Math.round(tt / 0.25 * 255); b = 255; }
                else if (tt < 0.5) { r = 0; g = 255; b = Math.round((1 - (tt - 0.25) / 0.25) * 255); }
                else if (tt < 0.75) { r = Math.round((tt - 0.5) / 0.25 * 255); g = 255; b = 0; }
                else { r = 255; g = Math.round((1 - (tt - 0.75) / 0.25) * 255); b = 0; }
                const j = i * 4;
                o[j] = r;
                o[j + 1] = g;
                o[j + 2] = b;
                o[j + 3] = Math.round(Math.min(tt * 1.4, 0.88) * 255);
            }
            gctx.putImageData(img, 0, 0);

            // Upscale the small grid to the display canvas with smoothing for soft blobs.
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, width, height);
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(grid, 0, 0, gw, gh, 0, 0, width, height);
        };

        requestAnimationFrame(render);

        let ro = null;
        if (typeof ResizeObserver !== 'undefined' && canvasRef.current) {
            ro = new ResizeObserver(() => requestAnimationFrame(render));
            ro.observe(canvasRef.current);
        }
        return () => { if (ro) ro.disconnect(); };
    }, [points]);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
        />
    );
}

// Per-question gaze heatmaps for the overview: a full-width frame with the
// pooled-participant heatmap on top, question tabs, and a Desktop/Mobile toggle
// (gaze is split by device since the responsive layout differs). Polls while the
// backend is still generating the desktop frames.
function OverviewGazeHeatmaps({ projectUuid, questionnaireUuid }) {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeIndex, setActiveIndex] = useState(0);
    const [preferredDevice, setPreferredDevice] = useState('desktop');

    useEffect(() => {
        if (!projectUuid || !questionnaireUuid) return undefined;
        let cancelled = false;
        let timer = null;

        const load = () => {
            getQuestionnaireGazeOverview(projectUuid, questionnaireUuid)
                .then((res) => {
                    if (cancelled) return;
                    setData(res);
                    setLoading(false);
                    // Desktop frames are generated asynchronously — poll until ready.
                    if (res?.frames_generating) {
                        timer = setTimeout(load, 5000);
                    }
                })
                .catch(() => { if (!cancelled) setLoading(false); });
        };
        load();

        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [projectUuid, questionnaireUuid]);

    if (loading || !data) return null;

    const questions = (data.questions ?? []).filter(q => q.desktop || q.mobile);
    if (questions.length === 0) return null;

    const activeI = Math.min(activeIndex, questions.length - 1);
    const active = questions[activeI];

    // Resolve which device view to show for the active question.
    const devices = [];
    if (active.desktop) devices.push('desktop');
    if (active.mobile) devices.push('mobile');
    const device = devices.includes(preferredDevice) ? preferredDevice : devices[0];
    const block = active[device];

    const deviceLabel = { desktop: t('Questionnaires.Results.DeviceDesktop'), mobile: t('Questionnaires.Results.DeviceMobile') };

    return (
        <Card>
            <div className="flex items-center gap-2 mb-1">
                <Title className="text-sm font-semibold">{t('Questionnaires.Results.SectionGazeHeatmaps')}</Title>
            </div>
            <Text className="text-xs text-gray-400 mb-3">{t('Questionnaires.Results.GazeHeatmapsDesc')}</Text>

            {questions.length > 1 && (
                <div className="flex gap-1.5 flex-wrap mb-3">
                    {questions.map((q, i) => (
                        <button
                            key={q.question_id}
                            onClick={() => setActiveIndex(i)}
                            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                                i === activeI
                                    ? 'bg-gray-900 text-white border-gray-900'
                                    : 'text-gray-600 border-gray-200 hover:border-gray-400'
                            }`}
                        >
                            Q{(q.order ?? i) + 1}
                        </button>
                    ))}
                </div>
            )}

            {devices.length > 1 && (
                <div className="inline-flex rounded-lg border border-gray-200 p-0.5 mb-4">
                    {devices.map((d) => (
                        <button
                            key={d}
                            onClick={() => setPreferredDevice(d)}
                            className={`text-xs px-3 py-1 rounded-md transition-colors ${
                                d === device ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            {deviceLabel[d]}
                        </button>
                    ))}
                </div>
            )}

            <div key={`${active.question_id}-${device}`} className="flex flex-col gap-2">
                <Text className="text-sm font-medium text-gray-700">
                    {`Q${(active.order ?? activeI) + 1}. ${active.question_text ?? ''}`}
                </Text>
                {block.frame_url ? (
                    // Full-width dark stage that centres the frame. The inner w-fit
                    // wrapper shrinks to the image so the inset-0 heatmap canvas
                    // overlays exactly the displayed image box (no letterboxing),
                    // while max-h caps tall portrait captures so they don't dominate.
                    <div className="w-full flex justify-center rounded-lg overflow-hidden border border-gray-200 bg-gray-900">
                        <div className="relative w-fit">
                            <img src={block.frame_url} alt="" className="block max-h-[65vh] w-auto max-w-full" />
                            {(block.gaze?.length ?? 0) > 0 && <StaticGazeHeatmap points={block.gaze} />}
                        </div>
                    </div>
                ) : (
                    <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-900 aspect-video flex items-center justify-center">
                        <span className="text-xs text-gray-400 px-3 text-center">
                            {t('Questionnaires.Results.GazeHeatmapGenerating')}
                        </span>
                    </div>
                )}
                <Text className="text-xs text-gray-400">
                    {t('Questionnaires.Results.GazeHeatmapParticipants', { count: block.participant_count ?? 0 })}
                </Text>
            </div>
        </Card>
    );
}

function OverviewTab({ analytics, projectUuid, questionnaireUuid }) {
    const { t } = useTranslation();
    const ov = analytics.overview;
    const [emotionAggregate, setEmotionAggregate] = useState(null);

    useEffect(() => {
        if (!projectUuid || !questionnaireUuid) return;
        getQuestionnaireVideoEmotionAggregate(projectUuid, questionnaireUuid)
            .then(data => setEmotionAggregate(data))
            .catch(() => {}); // non-critical — overview still works without emotion data
    }, [projectUuid, questionnaireUuid]);

    const completionData = [
        { name: 'Completed', value: ov.completed_responses ?? 0 },
        { name: 'In Progress / Abandoned', value: (ov.total_responses ?? 0) - (ov.completed_responses ?? 0) },
    ].filter(d => d.value > 0);

    const trendData = (analytics.response_trends ?? [])
        .slice()
        .reverse()
        .reduce((acc, row) => {
            const existing = acc.find(d => d.date === row.date);
            if (existing) {
                existing[row.status] = (existing[row.status] ?? 0) + row.count;
            } else {
                acc.push({ date: row.date, [row.status]: row.count });
            }
            return acc;
        }, []);

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <StatBox label={t('Questionnaires.Results.StatTotalResponses')} value={ov.total_responses ?? 0} />
                <StatBox label={t('Questionnaires.Results.StatCompleted')} value={ov.completed_responses ?? 0} />
                <StatBox
                    label={t('Questionnaires.Results.StatCompletionRate')}
                    value={ov.completion_rate != null ? `${Math.round(ov.completion_rate)}%` : '—'}
                />
                <StatBox
                    label={t('Questionnaires.Results.StatAvgDuration')}
                    value={formatMs(ov.avg_total_duration)}
                    sub={t('Questionnaires.Results.StatPerCompletedResponse')}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {completionData.length > 0 && (
                    <Card>
                        <Title className="text-sm font-semibold mb-3">{t('Questionnaires.Results.SectionResponseCompletion')}</Title>
                        <DonutChart
                            className="h-40"
                            data={completionData}
                            category="value"
                            index="name"
                            colors={['zinc', 'slate']}
                            showAnimation
                            valueFormatter={(v) => `${v} responses`}
                        />
                    </Card>
                )}
                <Card>
                    <Title className="text-sm font-semibold mb-3">{t('Questionnaires.Results.SectionRecordingSessions')}</Title>
                    <div className="flex flex-col gap-3 mt-2">
                        <div className="flex items-center justify-between">
                            <Text className="text-sm text-gray-600">{t('Questionnaires.Results.StatTotalSessions')}</Text>
                            <Text className="font-semibold">{ov.recordings_overview?.total_sessions ?? 0}</Text>
                        </div>
                        <div className="flex items-center justify-between">
                            <Text className="text-sm text-gray-600">{t('Questionnaires.Results.StatWithScreenRecording')}</Text>
                            <Text className="font-semibold">{ov.recordings_overview?.sessions_with_screen ?? 0}</Text>
                        </div>
                        <div className="flex items-center justify-between">
                            <Text className="text-sm text-gray-600">{t('Questionnaires.Results.StatQuestions')}</Text>
                            <Text className="font-semibold">{ov.questions_count ?? 0}</Text>
                        </div>
                    </div>
                </Card>
            </div>

            {trendData.length > 0 && (
                <Card>
                    <Title className="text-sm font-semibold mb-3">{t('Questionnaires.Results.SectionResponseTrends')}</Title>
                    <LineChart
                        className="h-48"
                        data={trendData}
                        index="date"
                        categories={['completed', 'in_progress', 'abandoned']}
                        colors={['zinc', 'amber', 'red']}
                        valueFormatter={(v) => `${v ?? 0}`}
                        showLegend
                        connectNulls
                        curveType="monotone"
                    />
                </Card>
            )}

            {emotionAggregate?.has_data && (
                <OverviewEmotionCard aggregate={emotionAggregate} />
            )}

            <OverviewInsightsCard analytics={analytics} projectUuid={projectUuid} questionnaireUuid={questionnaireUuid} />

            <OverviewConstructs analytics={analytics} projectUuid={projectUuid} questionnaireUuid={questionnaireUuid} />

            <OverviewGazeHeatmaps projectUuid={projectUuid} questionnaireUuid={questionnaireUuid} />
        </div>
    );
}

const EMOTION_HEX = {
    happiness: '#10b981', sadness: '#3b82f6', anger: '#ef4444',
    fear: '#8b5cf6', surprise: '#f97316', disgust: '#65a30d', neutral: '#6b7280',
};
const EMOTION_TREMOR = {
    happiness: 'emerald', sadness: 'blue', anger: 'red',
    fear: 'violet', surprise: 'orange', disgust: 'lime', neutral: 'gray',
};
const EMOTION_LABEL = {
    happiness: 'Happiness', sadness: 'Sadness', anger: 'Anger',
    fear: 'Fear', surprise: 'Surprise', disgust: 'Disgust', neutral: 'Neutral',
};

function OverviewEmotionCard({ aggregate }) {
    const { t } = useTranslation();
    const emotions = ['happiness', 'sadness', 'anger', 'fear', 'surprise', 'disgust', 'neutral'];
    const donutData = emotions.map(em => ({
        name: EMOTION_LABEL[em],
        value: aggregate.emotion_averages[em] ?? 0,
        emotion: em,
    })).filter(d => d.value > 0);

    return (
        <Card>
            <div className="flex items-center gap-2 mb-1">
                <Title className="text-sm font-semibold">{t('Questionnaires.Results.SectionFacialEmotion')}</Title>
            </div>
            <Text className="text-xs text-gray-400 mb-4">
                Average across all participants &amp; questions — {aggregate.completed_segments} segment{aggregate.completed_segments !== 1 ? 's' : ''} analysed
            </Text>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div>
                    {aggregate.dominant_emotion && (
                        <p className="text-lg font-semibold text-gray-800 mb-3 capitalize">
                            {EMOTION_LABEL[aggregate.dominant_emotion] ?? aggregate.dominant_emotion}
                        </p>
                    )}
                    <DonutChart
                        className="h-56"
                        data={donutData}
                        category="value"
                        index="name"
                        colors={donutData.map(d => EMOTION_TREMOR[d.emotion] ?? 'gray')}
                        variant="pie"
                        valueFormatter={v => `${v}%`}
                        showAnimation
                        showLegend={false}
                        showTooltip
                    />
                </div>

                <div className="flex flex-col gap-2">
                    {emotions.map(em => {
                        const pct = aggregate.emotion_averages[em] ?? 0;
                        return (
                            <div key={em} className="flex items-center gap-3">
                                <div className="w-3 h-3 rounded shrink-0" style={{ backgroundColor: EMOTION_HEX[em] }} />
                                <Text className="text-sm text-gray-700 w-24 shrink-0">{EMOTION_LABEL[em]}</Text>
                                <div className="flex-1 bg-gray-100 rounded-full h-2">
                                    <div
                                        className="h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${pct}%`, backgroundColor: EMOTION_HEX[em] }}
                                    />
                                </div>
                                <Text className="text-xs text-gray-500 w-10 text-right shrink-0">{pct}%</Text>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Card>
    );
}

function QuestionsTab({ analytics }) {
    const { t } = useTranslation();
    const questions = analytics.question_analytics ?? [];

    if (questions.length === 0) {
        return <Text className="text-gray-500 text-center py-12">{t('Questionnaires.Results.NoQuestionData')}</Text>;
    }

    const timingData = questions.map(q => ({
        question: q.question_text.length > 40 ? q.question_text.slice(0, 40) + '…' : q.question_text,
        'Avg (s)': q.timing?.avg_duration_ms != null ? +(q.timing.avg_duration_ms / 1000).toFixed(1) : 0,
        'Min (s)': q.timing?.min_duration_ms != null ? +(q.timing.min_duration_ms / 1000).toFixed(1) : 0,
        'Max (s)': q.timing?.max_duration_ms != null ? +(q.timing.max_duration_ms / 1000).toFixed(1) : 0,
    }));

    const engagementData = questions.map(q => ({
        question: q.question_text.length > 40 ? q.question_text.slice(0, 40) + '…' : q.question_text,
        'Avg clicks': q.timing?.avg_click_count ?? 0,
        'Avg mouse moves': q.timing?.avg_move_count != null ? +(q.timing.avg_move_count / 10).toFixed(1) : 0,
    }));

    return (
        <div className="space-y-6">
            <Card>
                <Title className="text-sm font-semibold mb-1">{t('Questionnaires.Results.SectionTimePerQuestion')}</Title>
                <Text className="text-xs text-gray-500 mb-4">Average, min and max time respondents spent on each question</Text>
                <BarChart
                    className="h-56"
                    data={timingData}
                    index="question"
                    categories={['Avg (s)', 'Min (s)', 'Max (s)']}
                    colors={['gray', 'zinc', 'slate']}
                    valueFormatter={(v) => `${v}s`}
                    showLegend
                    layout="vertical"
                />
            </Card>

            <Card>
                <Title className="text-sm font-semibold mb-1">{t('Questionnaires.Results.SectionInteractionPerQuestion')}</Title>
                <Text className="text-xs text-gray-500 mb-4">Average clicks and mouse movement (×10 moves) while viewing each question</Text>
                <BarChart
                    className="h-56"
                    data={engagementData}
                    index="question"
                    categories={['Avg clicks', 'Avg mouse moves']}
                    colors={['amber', 'emerald']}
                    showLegend
                    layout="vertical"
                />
            </Card>

            {questions.map(q => (
                <Card key={q.id}>
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 pr-4">
                            <Text className="text-xs text-gray-400 uppercase tracking-wide">{q.type}</Text>
                            <p className="font-medium text-gray-900 text-sm mt-1">{q.question_text}</p>
                        </div>
                        <div className="text-right shrink-0">
                            <Text className="text-xs text-gray-500">{t('Questionnaires.Results.QuestionResponses', { count: q.responses_count })}</Text>
                            {q.skip_rate > 0 && (
                                <Text className="text-xs text-amber-600">{t('Questionnaires.Results.QuestionSkipped', { count: q.skip_rate })}</Text>
                            )}
                        </div>
                    </div>

                    {q.timing?.avg_duration_ms != null && (
                        <div className="flex gap-4 mb-4 text-xs text-gray-600">
                            <span>⏱ Avg: <strong>{formatMs(q.timing.avg_duration_ms)}</strong></span>
                            <span>Min: <strong>{formatMs(q.timing.min_duration_ms)}</strong></span>
                            <span>Max: <strong>{formatMs(q.timing.max_duration_ms)}</strong></span>
                            {q.timing.avg_click_count > 0 && (
                                <span>Avg clicks: <strong>{q.timing.avg_click_count}</strong></span>
                            )}
                        </div>
                    )}

                    {q.options_analytics?.length > 0 && (
                        <BarChart
                            className="h-36"
                            data={q.options_analytics.map(o => ({
                                option: o.text?.length > 35 ? o.text.slice(0, 35) + '…' : (o.text || '—'),
                                'Selections': o.selection_count ?? 0,
                            }))}
                            index="option"
                            categories={['Selections']}
                            colors={['gray']}
                            valueFormatter={(v) => `${v}`}
                            showLegend={false}
                            layout="vertical"
                        />
                    )}

                    {q.voice_answers?.length > 0 && (
                        <div className="space-y-3 mt-4">
                            <Text className="text-xs text-gray-500 uppercase tracking-wide">
                                {t('Questionnaires.Results.VoiceAnswers')}
                            </Text>
                            {q.voice_answers.map((answer, i) => (
                                <div key={answer.response_uuid || i} className="border border-gray-200 rounded-lg p-3 space-y-2">
                                    <audio src={answer.audio_url} controls className="w-full" />
                                    {answer.transcription_status === 'completed' ? (
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{answer.transcript}</p>
                                    ) : (
                                        <Text className="text-xs text-gray-500">
                                            {answer.transcription_status === 'failed'
                                                ? t('Questionnaires.Results.TranscriptionFailed')
                                                : t('Questionnaires.Results.Transcribing')}
                                        </Text>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            ))}
        </div>
    );
}

function MarketInsightsTab({ analytics }) {
    const { t } = useTranslation();
    const questions = analytics.question_analytics ?? [];
    if (questions.length === 0) {
        return <Text className="text-gray-500 text-center py-12">{t('Questionnaires.Results.NoDataAvailable')}</Text>;
    }

    const hesitationData = questions.map(q => ({
        question: q.question_text.length > 40 ? q.question_text.slice(0, 40) + '…' : q.question_text,
        'Hesitation (s)': q.timing?.avg_duration_ms != null ? +(q.timing.avg_duration_ms / 1000).toFixed(1) : 0,
    }));

    const reconsiderationData = questions.map(q => ({
        question: q.question_text.length > 40 ? q.question_text.slice(0, 40) + '…' : q.question_text,
        'Avg clicks (reconsiderations)': q.timing?.avg_click_count ?? 0,
    }));

    const skipData = questions.map(q => ({
        question: q.question_text.length > 40 ? q.question_text.slice(0, 40) + '…' : q.question_text,
        'Skip rate': q.responses_count > 0
            ? +((q.skip_rate / q.responses_count) * 100).toFixed(1)
            : 0,
    }));

    const mostHesitated = [...questions].sort((a, b) => (b.timing?.avg_duration_ms ?? 0) - (a.timing?.avg_duration_ms ?? 0))[0];
    const mostClicked = [...questions].sort((a, b) => (b.timing?.avg_click_count ?? 0) - (a.timing?.avg_click_count ?? 0))[0];
    const highestSkip = [...questions].sort((a, b) => {
        const ra = a.responses_count > 0 ? a.skip_rate / a.responses_count : 0;
        const rb = b.responses_count > 0 ? b.skip_rate / b.responses_count : 0;
        return rb - ra;
    })[0];

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="bg-gray-50 border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                        <ClockIcon className="h-4 w-4 text-gray-700" />
                        <Text className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{t('Questionnaires.Results.MetricMostHesitated')}</Text>
                    </div>
                    <p className="text-sm font-medium text-gray-800 mb-1 line-clamp-2">{mostHesitated?.question_text}</p>
                    <Text className="text-xs text-gray-500">{t('Questionnaires.Results.MetricAvgTime', { time: formatMs(mostHesitated?.timing?.avg_duration_ms) })}</Text>
                    <Text className="text-xs text-gray-400 mt-2">{t('Questionnaires.Results.MetricHesitationHint')}</Text>
                </Card>

                <Card className="bg-amber-50 border-amber-100">
                    <div className="flex items-center gap-2 mb-2">
                        <CursorArrowRaysIcon className="h-4 w-4 text-amber-600" />
                        <Text className="text-xs font-semibold text-amber-700 uppercase tracking-wide">{t('Questionnaires.Results.MetricMostReconsidered')}</Text>
                    </div>
                    <p className="text-sm font-medium text-gray-800 mb-1 line-clamp-2">{mostClicked?.question_text}</p>
                    <Text className="text-xs text-gray-500">{t('Questionnaires.Results.MetricAvgClicks', { count: mostClicked?.timing?.avg_click_count ?? '—' })}</Text>
                    <Text className="text-xs text-gray-400 mt-2">{t('Questionnaires.Results.MetricReconsiderationHint')}</Text>
                </Card>

                <Card className="bg-red-50 border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                        <QuestionMarkCircleIcon className="h-4 w-4 text-red-500" />
                        <Text className="text-xs font-semibold text-red-700 uppercase tracking-wide">{t('Questionnaires.Results.MetricHighestSkipRate')}</Text>
                    </div>
                    <p className="text-sm font-medium text-gray-800 mb-1 line-clamp-2">{highestSkip?.question_text}</p>
                    <Text className="text-xs text-gray-500">
                        {t('Questionnaires.Results.MetricSkipRate', {
                            rate: highestSkip?.responses_count > 0
                                ? ((highestSkip.skip_rate / highestSkip.responses_count) * 100).toFixed(1)
                                : '—'
                        })}
                    </Text>
                    <Text className="text-xs text-gray-400 mt-2">{t('Questionnaires.Results.MetricSkipHint')}</Text>
                </Card>
            </div>

            <Card>
                <Title className="text-sm font-semibold mb-1">{t('Questionnaires.Results.SectionHesitationPerQuestion')}</Title>
                <Text className="text-xs text-gray-500 mb-4">
                    Time-to-answer proxy: longer time suggests higher cognitive load or uncertainty.
                </Text>
                <BarChart
                    className="h-52"
                    data={hesitationData}
                    index="question"
                    categories={['Hesitation (s)']}
                    colors={['gray']}
                    valueFormatter={(v) => `${v}s`}
                    showLegend={false}
                    layout="vertical"
                />
            </Card>

            <Card>
                <Title className="text-sm font-semibold mb-1">{t('Questionnaires.Results.SectionReconsiderationPerQuestion')}</Title>
                <Text className="text-xs text-gray-500 mb-4">
                    Average number of clicks while on each question. Multiple clicks signal ambivalence.
                </Text>
                <BarChart
                    className="h-52"
                    data={reconsiderationData}
                    index="question"
                    categories={['Avg clicks (reconsiderations)']}
                    colors={['amber']}
                    valueFormatter={(v) => `${v}`}
                    showLegend={false}
                    layout="vertical"
                />
            </Card>

            <Card>
                <Title className="text-sm font-semibold mb-1">{t('Questionnaires.Results.SectionSkipRatePerQuestion')}</Title>
                <Text className="text-xs text-gray-500 mb-4">
                    Questions with high skip rates may be too sensitive, poorly worded, or irrelevant.
                </Text>
                <BarChart
                    className="h-52"
                    data={skipData}
                    index="question"
                    categories={['Skip rate']}
                    colors={['red']}
                    valueFormatter={(v) => `${v}%`}
                    showLegend={false}
                    layout="vertical"
                />
            </Card>

            <Card className="bg-gray-50">
                <Title className="text-sm font-semibold mb-3">{t('Questionnaires.Results.InterpretationGuideTitle')}</Title>
                <div className="space-y-2 text-xs text-gray-600">
                    <p><strong>{t('Questionnaires.Results.InterpretHesitationTitle')}</strong>: {t('Questionnaires.Results.InterpretHesitationDesc')}</p>
                    <p><strong>{t('Questionnaires.Results.InterpretReconsiderationTitle')}</strong>: {t('Questionnaires.Results.InterpretReconsiderationDesc')}</p>
                    <p><strong>{t('Questionnaires.Results.InterpretSkipRateTitle')}</strong>: {t('Questionnaires.Results.InterpretSkipRateDesc')}</p>
                    <p><strong>{t('Questionnaires.Results.InterpretMouseMovesTitle')}</strong>: {t('Questionnaires.Results.InterpretMouseMovesDesc')}</p>
                </div>
            </Card>
        </div>
    );
}

const TAB_KEYS = [
    'Questionnaires.Results.TabOverview',
    'Questionnaires.Results.TabQuestions',
    'Questionnaires.Results.TabResponses',
    'Questionnaires.Results.TabMarketInsights',
];

function QuestionnaireResultsPage() {
    const { t } = useTranslation();
    const { project_uuid, questionnaire_uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { isCollapsed } = useSidebar();

    const [activeTab, setActiveTab] = useState(0);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const title = location.state?.title ?? t('Questionnaires.Results.SectionAnalytics');

    useEffect(() => {
        getQuestionnaireAnalytics(project_uuid, questionnaire_uuid)
            .then(data => setAnalytics(data))
            .catch(() => setError(t('Questionnaires.Results.ErrorLoadAnalytics')))
            .finally(() => setLoading(false));
    }, [project_uuid, questionnaire_uuid]);

    return (
        <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 py-12 w-full flex justify-center`}>
            <div className="flex flex-col w-full justify-center items-center gap-4">
                {/* Card 1: Header */}
                <div className="bg-white max-w-7xl rounded-2xl w-full shadow-xl mx-8">
                    <SectionHeader
                        title={title}
                        subtitle={t('Questionnaires.Results.SectionAnalytics')}
                        breadcrubs={[
                            { name: t('Questionnaires.Results.Breadcrumb'), href: `/project/${project_uuid}` },
                            { name: title },
                        ]}
                    />
                </div>

                {/* Card 2: Tabs + content */}
                <div className="bg-white max-w-7xl rounded-2xl w-full shadow-xl mx-8 mb-8">
                    <div className="px-6 pt-5 pb-4">
                        <nav className="flex gap-2">
                            {TAB_KEYS.map((tabKey, i) => (
                                <button
                                    key={tabKey}
                                    onClick={() => setActiveTab(i)}
                                    className={`rounded-md px-3 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                                        activeTab === i
                                            ? 'bg-iavisus-100 text-white'
                                            : 'border border-iavisus-100 text-iavisus-100 hover:bg-iavisus-100 hover:text-white'
                                    }`}
                                >
                                    {t(tabKey)}
                                </button>
                            ))}
                        </nav>
                    </div>

                    <div className="px-6 pb-6">
                        {loading && (
                            <div className="flex items-center justify-center py-24">
                                <ArrowPathIcon className="h-6 w-6 animate-spin text-gray-400 mr-2" />
                                <Text>{t('Questionnaires.Results.LoadingAnalytics')}</Text>
                            </div>
                        )}
                        {error && <Text className="text-red-500 text-center py-12">{error}</Text>}
                        {!loading && !error && analytics && (
                            <>
                                {activeTab === 0 && <OverviewTab analytics={analytics} projectUuid={project_uuid} questionnaireUuid={questionnaire_uuid} />}
                                {activeTab === 1 && <QuestionsTab analytics={analytics} />}
                                {activeTab === 2 && (
                                    <ResponsesTab
                                        projectUuid={project_uuid}
                                        questionnaireUuid={questionnaire_uuid}
                                    />
                                )}
                                {activeTab === 3 && <MarketInsightsTab analytics={analytics} />}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuestionnaireResultsPage;
